/**
 * Configuration Comparison Component
 *
 * Displays side-by-side comparison of two configurations with diff highlighting.
 */

import { useState, useMemo } from 'react';
import {
  ArrowLeftRight,
  Server,
  Network,
  Shield,
  Package,
  CheckCircle2,
  XCircle,
  Plus,
  Minus,
  RefreshCw,
} from 'lucide-react';
import { Button, Card } from '../ui';
import { DatacenterConfig } from '../../types/configurator';
import { clsx } from 'clsx';

interface ConfigComparisonProps {
  configA: DatacenterConfig;
  configB: DatacenterConfig;
  labelA?: string;
  labelB?: string;
  onClose?: () => void;
}

interface DiffResult {
  field: string;
  category: 'metadata' | 'network' | 'nodes' | 'services' | 'security';
  type: 'added' | 'removed' | 'modified' | 'unchanged';
  valueA?: string | number | boolean;
  valueB?: string | number | boolean;
  details?: string;
}

function compareConfigs(configA: DatacenterConfig, configB: DatacenterConfig): DiffResult[] {
  const diffs: DiffResult[] = [];

  // Metadata
  if (configA.name !== configB.name) {
    diffs.push({
      field: 'Name',
      category: 'metadata',
      type: 'modified',
      valueA: configA.name,
      valueB: configB.name,
    });
  }

  if (configA.scenario !== configB.scenario) {
    diffs.push({
      field: 'Scenario',
      category: 'metadata',
      type: 'modified',
      valueA: configA.scenario,
      valueB: configB.scenario,
    });
  }

  // Network
  const networkFields: (keyof typeof configA.network)[] = [
    'subnet',
    'gateway',
    'netmask',
    'domain',
    'dhcpRangeStart',
    'dhcpRangeEnd',
  ];

  for (const field of networkFields) {
    if (configA.network[field] !== configB.network[field]) {
      diffs.push({
        field: `Network ${field}`,
        category: 'network',
        type: 'modified',
        valueA: configA.network[field] as string,
        valueB: configB.network[field] as string,
      });
    }
  }

  // Nodes
  const nodeMapA = new Map(configA.nodes.map((n) => [n.id, n]));
  const nodeMapB = new Map(configB.nodes.map((n) => [n.id, n]));

  // Find added nodes (in B but not in A)
  for (const [id, node] of nodeMapB) {
    if (!nodeMapA.has(id)) {
      diffs.push({
        field: `Node: ${node.hostname}`,
        category: 'nodes',
        type: 'added',
        valueB: `${node.type} (${node.ip})`,
        details: `${node.specs.cpuCores} CPU, ${node.specs.memoryGB}GB RAM`,
      });
    }
  }

  // Find removed nodes (in A but not in B)
  for (const [id, node] of nodeMapA) {
    if (!nodeMapB.has(id)) {
      diffs.push({
        field: `Node: ${node.hostname}`,
        category: 'nodes',
        type: 'removed',
        valueA: `${node.type} (${node.ip})`,
        details: `${node.specs.cpuCores} CPU, ${node.specs.memoryGB}GB RAM`,
      });
    }
  }

  // Find modified nodes
  for (const [id, nodeA] of nodeMapA) {
    const nodeB = nodeMapB.get(id);
    if (nodeB) {
      const changes: string[] = [];
      if (nodeA.hostname !== nodeB.hostname) changes.push(`hostname: ${nodeA.hostname} → ${nodeB.hostname}`);
      if (nodeA.ip !== nodeB.ip) changes.push(`ip: ${nodeA.ip} → ${nodeB.ip}`);
      if (nodeA.type !== nodeB.type) changes.push(`type: ${nodeA.type} → ${nodeB.type}`);
      if (nodeA.specs.cpuCores !== nodeB.specs.cpuCores) changes.push(`CPU: ${nodeA.specs.cpuCores} → ${nodeB.specs.cpuCores}`);
      if (nodeA.specs.memoryGB !== nodeB.specs.memoryGB) changes.push(`RAM: ${nodeA.specs.memoryGB}GB → ${nodeB.specs.memoryGB}GB`);
      if (nodeA.enabled !== nodeB.enabled) changes.push(`enabled: ${nodeA.enabled} → ${nodeB.enabled}`);

      if (changes.length > 0) {
        diffs.push({
          field: `Node: ${nodeA.hostname}`,
          category: 'nodes',
          type: 'modified',
          details: changes.join(', '),
        });
      }
    }
  }

  // Services
  const servicesA = new Set(configA.serviceAllocations.map((s) => `${s.serviceId}@${s.nodeId}`));
  const servicesB = new Set(configB.serviceAllocations.map((s) => `${s.serviceId}@${s.nodeId}`));

  for (const service of servicesB) {
    if (!servicesA.has(service)) {
      const [serviceId, nodeId] = service.split('@');
      const nodeB = configB.nodes.find((n) => n.id === nodeId);
      diffs.push({
        field: `Service: ${serviceId}`,
        category: 'services',
        type: 'added',
        valueB: `on ${nodeB?.hostname || nodeId}`,
      });
    }
  }

  for (const service of servicesA) {
    if (!servicesB.has(service)) {
      const [serviceId, nodeId] = service.split('@');
      const nodeA = configA.nodes.find((n) => n.id === nodeId);
      diffs.push({
        field: `Service: ${serviceId}`,
        category: 'services',
        type: 'removed',
        valueA: `on ${nodeA?.hostname || nodeId}`,
      });
    }
  }

  // Security
  if (configA.security.vpnEnabled !== configB.security.vpnEnabled) {
    diffs.push({
      field: 'VPN Enabled',
      category: 'security',
      type: 'modified',
      valueA: configA.security.vpnEnabled ? 'Yes' : 'No',
      valueB: configB.security.vpnEnabled ? 'Yes' : 'No',
    });
  }

  if (configA.security.encryptStorage !== configB.security.encryptStorage) {
    diffs.push({
      field: 'Storage Encryption',
      category: 'security',
      type: 'modified',
      valueA: configA.security.encryptStorage ? 'Yes' : 'No',
      valueB: configB.security.encryptStorage ? 'Yes' : 'No',
    });
  }

  if (configA.security.auditLogging !== configB.security.auditLogging) {
    diffs.push({
      field: 'Audit Logging',
      category: 'security',
      type: 'modified',
      valueA: configA.security.auditLogging ? 'Yes' : 'No',
      valueB: configB.security.auditLogging ? 'Yes' : 'No',
    });
  }

  if (configA.security.firewallDefaultDeny !== configB.security.firewallDefaultDeny) {
    diffs.push({
      field: 'Firewall Default Deny',
      category: 'security',
      type: 'modified',
      valueA: configA.security.firewallDefaultDeny ? 'Yes' : 'No',
      valueB: configB.security.firewallDefaultDeny ? 'Yes' : 'No',
    });
  }

  // VPN Peers
  const peersA = new Set(configA.security.peers.map((p) => p.id));
  const peersB = new Set(configB.security.peers.map((p) => p.id));

  for (const peer of configB.security.peers) {
    if (!peersA.has(peer.id)) {
      diffs.push({
        field: `VPN Peer: ${peer.name}`,
        category: 'security',
        type: 'added',
        valueB: peer.allowedIPs,
      });
    }
  }

  for (const peer of configA.security.peers) {
    if (!peersB.has(peer.id)) {
      diffs.push({
        field: `VPN Peer: ${peer.name}`,
        category: 'security',
        type: 'removed',
        valueA: peer.allowedIPs,
      });
    }
  }

  return diffs;
}

export function ConfigComparison({
  configA,
  configB,
  labelA = 'Configuration A',
  labelB = 'Configuration B',
  onClose,
}: ConfigComparisonProps) {
  const [filter, setFilter] = useState<DiffResult['category'] | 'all'>('all');

  const diffs = useMemo(() => compareConfigs(configA, configB), [configA, configB]);

  const filteredDiffs = useMemo(() => {
    if (filter === 'all') return diffs;
    return diffs.filter((d) => d.category === filter);
  }, [diffs, filter]);

  const summary = useMemo(() => {
    return {
      added: diffs.filter((d) => d.type === 'added').length,
      removed: diffs.filter((d) => d.type === 'removed').length,
      modified: diffs.filter((d) => d.type === 'modified').length,
      total: diffs.length,
    };
  }, [diffs]);

  const getCategoryIcon = (category: DiffResult['category']) => {
    switch (category) {
      case 'metadata':
        return <RefreshCw className="h-4 w-4" />;
      case 'network':
        return <Network className="h-4 w-4" />;
      case 'nodes':
        return <Server className="h-4 w-4" />;
      case 'services':
        return <Package className="h-4 w-4" />;
      case 'security':
        return <Shield className="h-4 w-4" />;
    }
  };

  const getTypeIcon = (type: DiffResult['type']) => {
    switch (type) {
      case 'added':
        return <Plus className="h-4 w-4 text-green-500" />;
      case 'removed':
        return <Minus className="h-4 w-4 text-red-500" />;
      case 'modified':
        return <RefreshCw className="h-4 w-4 text-blue-500" />;
      case 'unchanged':
        return <CheckCircle2 className="h-4 w-4 text-gray-400" />;
    }
  };

  const getTypeBgColor = (type: DiffResult['type']) => {
    switch (type) {
      case 'added':
        return 'bg-green-50 border-green-200';
      case 'removed':
        return 'bg-red-50 border-red-200';
      case 'modified':
        return 'bg-blue-50 border-blue-200';
      case 'unchanged':
        return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <ArrowLeftRight className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Configuration Comparison</h2>
            <p className="text-sm text-gray-500">
              Comparing <span className="font-medium">{labelA}</span> with{' '}
              <span className="font-medium">{labelB}</span>
            </p>
          </div>
        </div>
        {onClose && (
          <Button variant="ghost" onClick={onClose}>
            <XCircle className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4 bg-green-50 border-green-200">
          <div className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-green-600" />
            <div>
              <p className="text-2xl font-bold text-green-700">{summary.added}</p>
              <p className="text-sm text-green-600">Added</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-red-50 border-red-200">
          <div className="flex items-center gap-2">
            <Minus className="h-5 w-5 text-red-600" />
            <div>
              <p className="text-2xl font-bold text-red-700">{summary.removed}</p>
              <p className="text-sm text-red-600">Removed</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-blue-600" />
            <div>
              <p className="text-2xl font-bold text-blue-700">{summary.modified}</p>
              <p className="text-sm text-blue-600">Modified</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-gray-500">Filter by:</span>
        {(['all', 'metadata', 'network', 'nodes', 'services', 'security'] as const).map((cat) => (
          <button
            key={cat}
            onClick={() => setFilter(cat)}
            className={clsx(
              'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
              filter === cat
                ? 'bg-indigo-100 text-indigo-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
          </button>
        ))}
      </div>

      {/* Diff List */}
      {filteredDiffs.length === 0 ? (
        <div className="text-center py-12">
          <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No differences found</h3>
          <p className="text-gray-500 mt-1">
            {filter === 'all'
              ? 'The configurations are identical'
              : `No differences in ${filter} settings`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredDiffs.map((diff, idx) => (
            <div
              key={idx}
              className={clsx(
                'rounded-lg border p-4',
                getTypeBgColor(diff.type)
              )}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">{getTypeIcon(diff.type)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs uppercase tracking-wide text-gray-500 flex items-center gap-1">
                      {getCategoryIcon(diff.category)}
                      {diff.category}
                    </span>
                  </div>
                  <p className="font-medium text-gray-900 mt-1">{diff.field}</p>
                  {(diff.valueA !== undefined || diff.valueB !== undefined) && (
                    <div className="mt-2 text-sm">
                      {diff.valueA !== undefined && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 w-16">{labelA}:</span>
                          <span className={clsx(
                            'px-2 py-0.5 rounded',
                            diff.type === 'removed' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                          )}>
                            {String(diff.valueA)}
                          </span>
                        </div>
                      )}
                      {diff.valueB !== undefined && (
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-gray-500 w-16">{labelB}:</span>
                          <span className={clsx(
                            'px-2 py-0.5 rounded',
                            diff.type === 'added' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          )}>
                            {String(diff.valueB)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  {diff.details && (
                    <p className="text-sm text-gray-600 mt-1">{diff.details}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats comparison */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Resource Comparison</h3>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-gray-500 mb-2">{labelA}</p>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Nodes</span>
                <span className="font-medium">{configA.nodes.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Power</span>
                <span className="font-medium">{configA.estimatedPower}W</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Est. Cost</span>
                <span className="font-medium">${configA.estimatedCost.toFixed(0)}</span>
              </div>
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-2">{labelB}</p>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Nodes</span>
                <span className="font-medium">{configB.nodes.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Power</span>
                <span className="font-medium">{configB.estimatedPower}W</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Est. Cost</span>
                <span className="font-medium">${configB.estimatedCost.toFixed(0)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
